import {
  IComponentRegistry,
  IComponentLoader,
  ILibraryComponents,
  ILibraryAsset,
  IRegistryAPI,
  IComponentAsset,
  IRegistryParameters,
} from '../registry.types';
import { createException } from '../errors/RegistryError';
import { getLibrariesArtifactIds } from '../libraries/loadAndParseLibraries';
import {
  filterStyleAssets,
  filterModelAssets,
  getComponentAssetsOrder,
  filterChunkAssets,
  filterWebpackRuntimeAsset,
} from './assets';

export type IComponentsLoaders<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
> = Record<string, IComponentLoader<TComponentModel, TParts>>;

export type IComponentsModels<TComponentModel> = Record<
  string,
  TComponentModel
>;

export type UnpackComponentModel<TComponentsLoaders> =
  TComponentsLoaders extends IComponentsLoaders<infer TComponentModel>
    ? TComponentModel
    : never;

function shouldIncludeLibrary(artifactId: string, artifactIds?: Array<string>) {
  if (!artifactIds) {
    return true;
  }

  return artifactIds.includes(artifactId);
}

function componentsShouldExist(
  components: IComponentsLoaders<any>,
  requestedComponents?: Array<string>,
) {
  if (requestedComponents) {
    const error = createException('component does not exist');

    let shouldThrow = false;

    requestedComponents.forEach(componentName => {
      if (!components[componentName]) {
        shouldThrow = true;
        error.withComponent(componentName);
      }
    });

    if (shouldThrow) {
      throw error;
    }
  }
}

async function loadComponents<
  TComponentsMap extends IComponentsLoaders<any>,
  TComponentModel = UnpackComponentModel<TComponentsMap>,
>(
  components: TComponentsMap,
  {
    requestedComponents,
  }: {
    requestedComponents?: Array<string>;
  } = {},
): Promise<IComponentsModels<TComponentModel>> {
  componentsShouldExist(components, requestedComponents);

  const componentNames = Object.keys(components).filter(componentName => {
    return !requestedComponents || requestedComponents.includes(componentName);
  });

  const pending = componentNames.map(async componentName => {
    return {
      componentName,
      model: await components[componentName](),
    };
  });

  const models = await Promise.all(pending);

  return models.reduce<IComponentsModels<TComponentModel>>(
    (acc, { model, componentName }) => {
      acc[componentName] = model;
      return acc;
    },
    {},
  );
}

export async function createRegistryAPI<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>(
  registry: IComponentRegistry<TComponentModel, TParts>,
  props: Pick<
    IRegistryParameters<TComponentModel, TParts>,
    | 'options'
    | 'mode'
    | 'modes'
    | 'libraries'
    | 'transport'
    | 'shouldLoadCSSAssets'
    | 'globals'
  > & {
    allowComponentsOverride?: boolean;
  } = {
    libraries: [],
  },
): Promise<IRegistryAPI<TComponentModel, TParts>> {
  const components: IComponentsLoaders<TComponentModel, TParts> = {};

  const libraries: Record<
    string,
    {
      components: ILibraryComponents<TComponentModel, TParts>;
      assets: Array<ILibraryAsset>;
    }
  > = {};

  const runtime = await registry({
    registerLibrary: (artifactId, libraryComponents, libraryAssets) => {
      if (libraries[artifactId]) {
        throw createException(
          'It is not possible to register library, it already exist',
        )
          .withDetails(`Library: ${artifactId}`)
          .withHint(
            'Probably, provided libraries with the same artifact id to the registry',
          );
      }

      libraries[artifactId] = {
        components: libraryComponents,
        assets: libraryAssets,
      };
    },
    ...props,
  });

  // NOTE: libraries should always exist
  const allowComponentsOverride = props.allowComponentsOverride || true;
  const artifactIds = getLibrariesArtifactIds(props.libraries);

  artifactIds.forEach(artifactId => {
    /**
     * libraries[artifactId] is empty when `registerLibrary` was not called with this `artifactId`.
     * the case – when there are no components for this `artifactId` in lib's manifest
     */
    if (!libraries[artifactId]) {
      return;
    }

    Object.keys(libraries[artifactId].components).forEach(componentName => {
      if (!allowComponentsOverride && components[componentName]) {
        throw createException(
          'It is not possible to register component loader, it already exist',
        )
          .withComponent(componentName)
          .withHint('Probably, component is duplicated in libraries');
      }

      components[componentName] =
        libraries[artifactId].components[componentName];
    });
  });

  /**
   * we need to define the `api` variable instead of `return` it,
   * because it is not possible to use `this` in case of `return`
   */
  const api: IRegistryAPI<TComponentModel, TParts> = {
    isComponentExist(name) {
      return components.hasOwnProperty(name);
    },
    getComponents() {
      return components;
    },
    getLibraries() {
      return libraries;
    },

    forEachLibrary(cb) {
      Object.keys(libraries).forEach(artifactId => {
        const library = libraries[artifactId];
        cb(artifactId, library.components, library.assets);
      });
    },

    forEachComponent(cb) {
      Object.keys(components).forEach(componentName => {
        cb(componentName, components[componentName]);
      });
    },

    getComponentStatics(componentName) {
      return components[componentName].statics;
    },

    getComponentsAssets(requestedComponents?) {
      return Object.keys(components)
        .filter(componentName => {
          return (
            !requestedComponents || requestedComponents.includes(componentName)
          );
        })
        .reduce((acc, componentName) => {
          /**
           * check if asset's url was already added
           * for cases when components are loaded within batch
           */
          const componentAssets = components[componentName].assets.filter(
            asset =>
              !acc.find(existingAsset => existingAsset.url === asset.url),
          );

          return [...acc, ...componentAssets];
        }, [] as Array<IComponentAsset>);
    },

    getComponentsModelJSAssets(requestedComponents) {
      const assets = filterModelAssets(
        this.getComponentsAssets(requestedComponents),
      );

      return getComponentAssetsOrder(assets) as Array<
        IComponentAsset<'model', 'js'>
      >;
    },

    getComponentsModelCSSAssets(requestedComponents) {
      const assets = filterStyleAssets(
        this.getComponentsAssets(requestedComponents),
      );

      return getComponentAssetsOrder(assets) as Array<
        IComponentAsset<'style', 'css'>
      >;
    },

    getCommonChunksJSAssets(requestedArtifactIds) {
      return Object.keys(libraries)
        .filter(artifactId =>
          shouldIncludeLibrary(artifactId, requestedArtifactIds),
        )
        .map(artifactId => {
          return filterChunkAssets(
            filterModelAssets(libraries[artifactId].assets),
          );
        })
        .flat() as Array<ILibraryAsset<'model', 'js'>>;
    },

    getCommonChunksCSSAssets(requestedArtifactIds) {
      return Object.keys(libraries)
        .filter(artifactId =>
          shouldIncludeLibrary(artifactId, requestedArtifactIds),
        )
        .map(artifactId => {
          return filterChunkAssets(
            filterStyleAssets(libraries[artifactId].assets),
          );
        })
        .flat() as Array<ILibraryAsset<'style', 'css'>>;
    },

    getHostModelJSAssets(requestedArtifactIds) {
      return Object.keys(libraries)
        .filter(artifactId =>
          shouldIncludeLibrary(artifactId, requestedArtifactIds),
        )
        .map(artifactId => {
          return filterModelAssets(libraries[artifactId].assets);
        })
        .flat() as Array<ILibraryAsset<'model', 'js'>>;
    },

    getHostModelCSSAssets(requestedArtifactIds) {
      return Object.keys(libraries)
        .filter(artifactId =>
          shouldIncludeLibrary(artifactId, requestedArtifactIds),
        )
        .map(artifactId => {
          return filterStyleAssets(libraries[artifactId].assets);
        })
        .flat() as Array<ILibraryAsset<'style', 'css'>>;
    },

    getLibrariesAssets() {
      return Object.keys(libraries).reduce((acc, artifactId) => {
        acc.push(...libraries[artifactId].assets);
        return acc;
      }, [] as Array<ILibraryAsset>);
    },

    getLibrariesCustomAssets() {
      return this.getLibrariesAssets().filter(({ type }) => {
        return !['hot', 'webpack-runtime', 'model', 'style'].includes(type);
      });
    },

    getLibrariesArtifactIds() {
      return Object.keys(libraries);
    },

    getLibrariesRuntimes() {
      return Object.keys(libraries).reduce((acc, artifactId) => {
        acc.push(
          ...filterWebpackRuntimeAsset(libraries[artifactId].assets).map(
            ({ url }) => url,
          ),
        );
        return acc;
      }, [] as Array<string>);
    },

    getRegistryRuntime() {
      return runtime;
    },

    loadComponent(componentName) {
      if (!this.isComponentExist(componentName)) {
        throw createException('Component does not exist').withComponent(
          componentName,
        );
      }

      return components[componentName]();
    },

    loadComponentPart(componentName, part) {
      if (!this.isComponentExist(componentName)) {
        throw createException('Component does not exist').withComponent(
          componentName,
        );
      }

      return components[componentName](part);
    },

    loadAllComponents() {
      return loadComponents(components);
    },

    loadComponents(requestedComponents) {
      return loadComponents(components, {
        requestedComponents,
      });
    },
  };

  return api;
}
